import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TimepointService } from '../../../../core/services/timepoint.service';
import { FormsModule, NgForm } from '@angular/forms';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { GbpersonService, PersonGbSearchEgbpidRequestMeta } from '../../../../core/gb-api';
import { AppConfigService } from '../../../../core/services/app-config.service';
import { PersonStammMasterDetailEntryMeta, PersonstammpersonService } from '../../../../core/personstamm-api';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { Observable, of, Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NavigationCheckModalComponent } from './navigation-check-modal/navigation-check-modal.component';
import { CapAlertService } from '../../../../core/extension/exceptions/cap-alert.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CapExceptionHandler } from '../../../../core/extension/exceptions/cap-exception-handler';
import { copy, copyMetaData } from '../../../../shared/functions/meta.functions';
import { ResolverValidationExceptionHandler } from '../../../../core/extension/exceptions/exception-handler-rule';
import { markFormAsTouched } from '../../persongb.functions';
import { SearchStateService } from '../../../../shared/services/search-state.service';
import { SearchMode } from '../../../../shared/enum/searchMode';
import { CapFormColumnsDirective } from '../../../../shared/form-control/form-columns.directive';
import { MetaDirective } from '../../../../shared/directives/meta.directive';
import { CapFormControlComponent } from '../../../../shared/form-control/form-control.component';
import { AutofocusDirective } from '../../../../shared/directives/autofocus.directive';
import { MetaDisablerDirective } from '../../../../shared/directives/meta-disabler.directive';
import { MetaValidatorDirective } from '../../../../shared/directives/meta-validator.directive';
import { CapSpinnerSmallComponent } from '../../../../shared/components/cap-spinner-small/cap-spinner-small.component';

interface PersonEgbpidResolver {
  loadDetail: LoadDetail;
  value: any;
}

enum LoadDetail {
  NotFound,
  PersonGb,
  PersonStamm
}

@Component({
  selector: 'cap-persongb-suche-egbpid',
  templateUrl: './person-by-egbpid-load-resolver.component.html',
  styleUrls: ['./person-by-egbpid-load-resolver.component.scss'],
  imports: [
    FormsModule,
    CapFormColumnsDirective,
    MetaDirective,
    CapFormControlComponent,
    AutofocusDirective,
    MetaDisablerDirective,
    MetaValidatorDirective,
    CapSpinnerSmallComponent,
    TranslatePipe
  ]
})
export class PersonByEgbpidLoadResolverComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('form', { static: false })
  form: NgForm;

  egbpid: PersonGbSearchEgbpidRequestMeta;

  loading = false;

  private subscription = new Subscription();

  readonly NOT_FOUND = <PersonEgbpidResolver>{ loadDetail: LoadDetail.NotFound };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private persongbService: GbpersonService,
    private zeitpunktService: TimepointService,
    private appConfigService: AppConfigService,
    private personstammAppService: PersonstammpersonService,
    private modalService: NgbModal,
    private stateService: SearchStateService,
    private translate: TranslateService,
    public capAlertService: CapAlertService,
    private exceptionHandler: CapExceptionHandler,
    private cd: ChangeDetectorRef,
    private resolverValidationExceptionHandler: ResolverValidationExceptionHandler
  ) {
    keysToTranslate(['EgbpidSuche', 'EingabeMussValideEgbpidSein']);
  }

  ngOnInit() {
    if (this.stateService.lastRequest && this.stateService.lastSearchMode === SearchMode.searchFachId) {
      this.egbpid = this.stateService.lastRequest;
    } else {
      this.egbpid = <PersonGbSearchEgbpidRequestMeta>{};
    }
    this.subscription.add(
      this.persongbService.searchPersonGbEgbpidTemplate().subscribe((template) => {
        if (this.stateService.lastRequest && this.stateService.lastSearchMode === SearchMode.searchFachId) {
          this.egbpid = copyMetaData(template, this.egbpid);
          return;
        }
        this.egbpid = copy(this.egbpid, template);
      })
    );
  }

  onSubmit(): void {
    this.zeitpunktService.resetTimepoint();
    this.stateService.lastRequest = this.egbpid;
    this.stateService.lastSearchMode = SearchMode.searchFachId;
    this.loading = true;

    this.subscription.add(
      this.resolvePersonByEgbpid().subscribe((result) => {
        this.loading = false;
        this.tryNavigateBy(result);
      })
    );
  }

  onReset(EGBPID): void {
    this.form.reset();
    this.subscription.add(
      this.persongbService.searchPersonGbEgbpidTemplate().subscribe((template) => {
        this.egbpid = template;
        EGBPID.focus();
      })
    );
  }

  private resolvePersonByEgbpid(): Observable<PersonEgbpidResolver | {}> {
    const personGb$ = this.persongbService
      .loadPersonGbByFachId(
        this.appConfigService.einstellungHistorischeDaten, //
        this.egbpid.egbpid.value, //
        this.zeitpunktService.timepointCtx.timepointIso, //
        this.zeitpunktService.timepointCtx.timepointId
      )
      .pipe(
        //
        map((entry) =>
          entry && entry.versions && entry.versions.length > 0 ? <PersonEgbpidResolver>{ loadDetail: LoadDetail.PersonGb } : this.NOT_FOUND
        ),
        catchError((err) => {
          if (err.status === 404) {
            return of(this.NOT_FOUND);
          }
          this.loading = false;
          if (this.resolverValidationExceptionHandler.errorHandled(err)) {
            return;
          }
          this.exceptionHandler.handleError(err);
        })
      );

    const stammperson$ = this.personstammAppService
      .getPersonstammDetailByFachId(
        this.egbpid.egbpid.value,
        this.appConfigService.einstellungHistorischeDaten, //
        this.zeitpunktService.timepointCtx.timepointIso, //
        null
      )
      .pipe(
        ////
        map((entry) =>
          entry && entry.version
            ? <PersonEgbpidResolver>{
                loadDetail: LoadDetail.PersonStamm,
                value: entry
              }
            : this.NOT_FOUND
        ), //
        catchError((err) => {
          if (err.status === 404) {
            return of(this.NOT_FOUND);
          }
          this.loading = false;
          if (this.resolverValidationExceptionHandler.errorHandled(err)) {
            return;
          }
          this.exceptionHandler.handleError(err);
        })
      );

    return personGb$.pipe(
      take(1),
      switchMap((resolver) => {
        if (resolver.loadDetail === LoadDetail.NotFound) {
          return stammperson$;
        }
        return of(resolver);
      })
    );
  }

  private tryNavigateBy(result) {
    markFormAsTouched(this.form);
    if (result.loadDetail === LoadDetail.NotFound) {
      this.capAlertService.info(this.translate.instant('KeineResultateGefunden'));
      return;
    }
    if (result.loadDetail === LoadDetail.PersonGb) {
      this.navigateToPersonGb();
      return;
    }
    if (result.loadDetail === LoadDetail.PersonStamm) {
      this.navigateToPersonstammByModal(result.value);
    }
  }

  private navigateToPersonstammByModal(personStammFound: PersonStammMasterDetailEntryMeta) {
    const modalRef = this.modalService.open(NavigationCheckModalComponent);
    modalRef.componentInstance.personStammMaster = personStammFound;
    modalRef.result.then(
      () => {
        this.navigateToPersonStamm();
      },
      () => {}
    );
  }

  private navigateToPersonGb() {
    this.router.navigate(['../persongb/detail'], {
      relativeTo: this.route,
      queryParams: { egbpid: this.egbpid.egbpid.value }
    });
  }

  private navigateToPersonStamm() {
    this.router.navigate(['../person/detail'], {
      relativeTo: this.route,
      queryParams: { egbpid: this.egbpid.egbpid.value }
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.cd.detectChanges();
  }
}
